import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Gift, Crown, Wallet } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";

const SLIDES = [
  { id: "welcome", eyebrow: "Welcome Offer", title: ["Double coins on", "your first bundle"], body: "Every first purchase is boosted 2× — plus free Sweeps Coins on top.", cta: "Get Coins", bg: "linear-gradient(120deg,#2a0f4a 0%,#5b21b6 55%,#1e0b3a 100%)", pick: ["diamond-power", "streak-777", "le-prechaun"] },
  { id: "jackpot", eyebrow: "Progressive Jackpots", title: ["Chase the", "GRAND jackpot"], body: "Four live jackpot tiers climbing every second across our slot floor.", cta: "Play Slots", bg: "linear-gradient(120deg,#3a1500 0%,#a14b00 55%,#2a0e00 100%)", pick: ["buffalo-extreme", "huff-house", "waylanders"] },
  { id: "live", eyebrow: "Live Casino", title: ["Take a seat at", "the live tables"], body: "Blackjack, Baccarat and Poker tables open 24/7 with fast payouts.", cta: "Open Tables", bg: "linear-gradient(120deg,#022b22 0%,#047857 55%,#011a14 100%)", pick: ["blackjack", "gravity-blackjack", "baccarat-live"] },
];

function Art({ games, ids }) {
  const imgs = ids.map((id) => games.find((g) => g.id === id)).filter(Boolean);
  return (
    <div className="hidden md:flex absolute right-6 lg:right-12 bottom-0 top-0 items-center gap-3">
      {imgs.map((g, i) => (
        <motion.img key={g.id} src={g.image} alt={g.name} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.08 }}
          className="w-28 lg:w-36 aspect-[180/236] rounded-xl object-cover shadow-[0_20px_40px_rgba(0,0,0,0.5)] ring-1 ring-white/10"
          style={{ transform: `rotate(${(i - 1) * 6}deg) translateY(${i === 1 ? -14 : 6}px)` }} />
      ))}
    </div>
  );
}

export function HeroCarousel({ games, onCta, onDaily }) {
  const { user } = useApp();
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % SLIDES.length), 6500); return () => clearInterval(t); }, []);
  const s = SLIDES[i];
  return (
    <section className="relative mt-5 overflow-hidden rounded-3xl border border-white/10 min-h-[240px] sm:min-h-[280px]" data-testid="hero-carousel">
      <AnimatePresence mode="wait">
        <motion.div key={s.id} className="absolute inset-0" style={{ background: s.bg }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
          <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.25), transparent 45%)" }} />
          <Art games={games} ids={s.pick} />
        </motion.div>
      </AnimatePresence>
      <div className="relative p-6 sm:p-10 max-w-lg" data-testid={`hero-slide-${s.id}`}>
        <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-white/70">{s.eyebrow} · Hi {user?.name?.split(" ")[0]}</div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95]">{s.title[0]} <span className="gc-text">{s.title[1]}</span></h1>
        <p className="mt-3 text-sm sm:text-base text-white/80 max-w-sm">{s.body}</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <button data-testid="hero-buy-btn" onClick={() => onCta(s.id)} className="rounded-full bg-[#FFB800] text-[#0B0E14] px-6 py-3 font-display font-extrabold hover:brightness-110 active:scale-95 transition-[filter,transform] shadow-[0_0_24px_rgba(255,184,0,0.4)]">{s.cta}</button>
          <button data-testid="daily-bonus-btn" onClick={onDaily} className="flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/15 px-6 py-3 font-display font-bold hover:bg-white/15 transition-colors"><Gift size={18} />Daily Bonus</button>
        </div>
      </div>
      <div className="absolute bottom-4 left-6 sm:left-10 flex gap-1.5">
        {SLIDES.map((x, k) => <button key={x.id} data-testid={`hero-dot-${k}`} onClick={() => setI(k)} className={`h-1.5 rounded-full transition-[width,background-color] ${k === i ? "w-6 bg-[#FFB800]" : "w-1.5 bg-white/40"}`} />)}
      </div>
    </section>
  );
}

export function PromoTiles({ onDaily }) {
  const { openStore } = useApp();
  const nav = useNavigate();
  const tiles = [
    ["promo-daily", "Daily Bonus", "5,000 GC + 0.50 SC free", Gift, "from-[#5b21b6] to-[#2e1065]", onDaily],
    ["promo-vip", "VIP Club", "Up to 25% bonus coins", Crown, "from-[#a16207] to-[#422006]", () => nav("/vip")],
    ["promo-redeem", "Instant Redeem", "Card, bank or USDC", Wallet, "from-[#047857] to-[#022c22]", () => openStore("redeem")],
  ];
  return (
    <div className="mt-4 grid grid-cols-3 gap-2.5 sm:gap-3" data-testid="promo-tiles">
      {tiles.map(([id, t, sub, I, g, fn]) => (
        <button key={id} data-testid={id} onClick={fn} className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${g} border border-white/10 p-3 sm:p-4 text-left hover:-translate-y-0.5 transition-transform`}>
          <I size={22} className="text-white/90 group-hover:scale-110 transition-transform" />
          <div className="mt-2 font-display font-black text-sm sm:text-base leading-tight">{t}</div>
          <div className="text-[10px] sm:text-xs text-white/70">{sub}</div>
        </button>
      ))}
    </div>
  );
}
